import { Card } from '@/components/ui';
import type { CargoCountResult } from '@/types/cargoCount.types';
import { CARGO_COUNT_COLORS } from '@/types/cargoCount.types';
import { CargoCountChartBar } from './CargoCountChartBar';
import { useMemo } from 'react';

interface CargoCountChartProps {
  header: string;
  cargoCount: CargoCountResult[];
}

export function CargoCountChart({ header, cargoCount }: CargoCountChartProps) {
  // Calculate max value for chart scaling
  const max = useMemo(() => {
    if (!cargoCount || cargoCount.length === 0) return 0;

    const highest = cargoCount.reduce((max, current) => {
      const { average, fixed, expected } = current;
      const newMax = Math.max(average, fixed, expected);
      return max < newMax ? newMax : max;
    }, 0);

    // Round off to nearest 5
    let maxRoundedOff = 5 * Math.round(highest / 5);
    if (maxRoundedOff < highest) {
      maxRoundedOff = maxRoundedOff + 5;
    }

    return maxRoundedOff;
  }, [cargoCount]);

  // Generate scale labels
  const scaleLabels = useMemo(() => {
    return [0, max * 0.2, max * 0.4, max * 0.6, max * 0.8, max];
  }, [max]);

  return (
    <Card noPadding className="overflow-hidden">
      {/* Card Header */}
      <div className="flex flex-col items-center gap-3 border-b border-gray-200 px-6 pb-4 pt-6 dark:border-gray-700 sm:flex-row sm:items-center sm:gap-4">
        <h2 className="m-0 mb-1 flex-1 text-xl font-semibold text-gray-900 dark:text-white sm:m-0">
          {header}
        </h2>
        <ul className="cargo-count-legend m-0 flex list-none flex-wrap justify-center gap-x-3 gap-y-2 p-0 sm:gap-x-4">
          <li className="flex items-center">
            <span
              className="mr-1 inline-block h-4 w-4 rounded-full"
              style={{ backgroundColor: CARGO_COUNT_COLORS.fixed }}
            ></span>
            <span className="text-sm text-gray-700 dark:text-gray-300">Fixed</span>
          </li>
          <li className="flex items-center">
            <span
              className="mr-1 inline-block h-4 w-4 rounded-full"
              style={{ backgroundColor: CARGO_COUNT_COLORS.expected }}
            ></span>
            <span className="text-sm text-gray-700 dark:text-gray-300">Expected</span>
          </li>
          <li className="flex items-center">
            <span
              className="mr-1 inline-block h-4 w-4 rounded-full border border-dashed"
              style={{
                backgroundColor: 'transparent',
                borderColor: CARGO_COUNT_COLORS.average,
              }}
            ></span>
            <span className="text-sm text-gray-700 dark:text-gray-300">Average</span>
          </li>
        </ul>
      </div>

      {/* Chart Bars */}
      <div className="px-6 pb-0 pt-4">
        {cargoCount.map((item, index) => (
          <CargoCountChartBar
            key={`${item.area}-${item.period}`}
            cargoCount={item}
            max={max}
            last={index === cargoCount.length - 1}
          />
        ))}
      </div>

      {/* Chart Footer with Scale */}
      <div className="flex px-6 pb-6 pt-0">
        <div className="w-[82px] min-w-[82px] flex-shrink-0" />
        <div className="chart-footer flex flex-1 justify-between border-t border-gray-300 text-sm text-gray-900 dark:border-gray-600 dark:text-gray-300 sm:text-base">
          {scaleLabels.map((label, index) => (
            <div
              key={index}
              className={`label relative flex flex-col font-medium opacity-80 before:inline-block before:h-2 before:w-px before:bg-gray-300 before:content-[''] before:dark:bg-gray-600 ${
                index === 0
                  ? 'before:self-start'
                  : index === scaleLabels.length - 1
                    ? 'before:self-end'
                    : 'before:self-end'
              }`}
            >
              {Math.round(label)}
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}
